/**
 * 取消预订
 */
import React, { useEffect, memo, useRef } from 'react';
import { connect } from 'react-redux';
import { message, Spin, Button } from 'antd';
import intl from 'react-intl-universal';

import { PriceChannel } from '@/config/constant';
import { PERM_CODES } from '@/config/permCode';
import usePortalDrawer, { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import PortalLoading from '@/components/PortalLoading';
import usePortalModal from '@/components/PortalConfirm';
import { showOnlyCloseModal, showSettlePrompt } from '@/components/Reservation/Modal';
import SettleDrawer, { SettleDrawerRef } from '@/components/Reservation/SettleDrawer';
import ThrottleButton from '@/components/Reservation/ThrottleButton';
import reserve from '@/api/reserve';
import { BizErrorCode } from '@/api/bizCode';
import { parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import getRandomId from '@/utils/uuid';
import { createStateSelector, findItemByKey } from '@/redux/actions/reservation/helper';
import {
  initPageState,
  setLoadingStatus,
  getMainState,
} from '@/redux/actions/reservation/cancelReserve';
import {
  ReducerState,
  RentRecordInfo,
  storeName,
} from '@/redux/reducers/reservation/cancelReserve';

import RentRoomList from './components/RentRoomList';
import validator from './validator';
import { showNoPermissionModal, showDirectCancelConfirm } from './components/Modal';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState, globalState) => {
  const { isInit, isLoading } = state;
  return { isInit, isLoading, permissions: globalState.user.permissions };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callInitState: initPageState,
  callSetLoading: setLoadingStatus,
  callGetState: getMainState,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface IConfig {
  mainOrderId: string; // 订单id
  recList: Array<string>; // 接待单列表
  onEnd?: (result: boolean) => void;
}
interface IProps extends MapStateProps, MapDispatchProps, IConfig {
  unmountNode: () => void;
}

const Index: React.FC<IProps> = (props) => {
  const { unmountNode, isInit, isLoading, callSetLoading, permissions } = props;
  const confirmPortal = usePortalModal();
  const settleDrawer = useRef<SettleDrawerRef>(null);

  useEffect(() => {
    const { mainOrderId, recList, callInitState } = props;
    if (mainOrderId && recList && recList.length) {
      // 初始化入住信息
      callInitState(() => {
        return reserve.getReserveCancelDetail({ resvNo: mainOrderId, recNos: recList }).then(
          (res) => {
            return {
              rentRecordList: (res.data || []).map((record) => {
                return {
                  ...record,
                  indexId: getRandomId(),
                  liquidatedDamages: parseFen2Yuan(record.liquidatedDamages), // 违约金
                };
              }),
            };
          },
          (e) => {
            unmountNode();
            throw e;
          },
        );
      });
    } else {
      message.error(intl.get('reservation_error_config_invalid'));
      unmountNode();
    }
  }, []);

  // 取消操作
  const handleEditCancel = () => {
    unmountNode();
  };

  // 校验结账状态
  const checkSettleStatus: (orderId: string) => Promise<void> = async (orderId) => {
    const isUnSettled = await showSettlePrompt(confirmPortal, orderId);
    if (isUnSettled) {
      // 未结账
      await new Promise<void>((resolve) => {
        settleDrawer.current.showDrawer(orderId, resolve);
      });
    }
  };

  // 提交数据
  const submitCancelData = async (recordList: RentRecordInfo[]) => {
    const { onEnd } = props;
    callSetLoading(true);
    try {
      // 同一个主单号
      const { resvNo } = findItemByKey(recordList, (item) => !!item.resvNo);
      const params = {
        cancelReservationItems: recordList.map((item) => {
          return {
            amount: parseYuan2Fen(item.liquidatedDamages),
            recNo: item.recNo,
          };
        }),
        resvNo,
      };
      const res = await reserve.cancelReserve(params).catch((errRes) => {
        if (errRes && errRes.code === BizErrorCode.RESERVE_ORDER_CANCELED) {
          // 订单已被取消了
          showOnlyCloseModal(confirmPortal, {
            title: intl.get('drawer_reserve_cancel_message_cancel_fail'),
            content: errRes.msg,
          });
        }
        throw errRes;
      });
      await checkSettleStatus(resvNo);
      const { crsOrderFlag } = res.data || {};
      message.success(intl.get('drawer_reserve_cancel_message_cancel_success'));
      if (crsOrderFlag) {
        message.success(intl.get('drawer_reserve_cancel_message_notify_success'));
      }
      if (onEnd) {
        onEnd(true);
      }
      unmountNode();
    } catch (e) {
      //
    }
    callSetLoading(false);
  };

  // 校验直连取消权限
  const checkDirectCancelPermission = () => {
    return permissions && permissions.includes(PERM_CODES.CANCEL_XC_ZL_RESERVE);
  };

  // 订单中包含直连渠道预订单
  const hasDirectChannel = (recordList: RentRecordInfo[]) => {
    let result = false;
    if (recordList?.length) {
      const directChannelCode = [PriceChannel.XC_ZL, PriceChannel.MT_ZL, PriceChannel.DY_ZL];
      result = recordList.some((item) => directChannelCode.includes(item.priceChannel));
    }
    return result;
  };

  // 提交取消预订
  const handleSubmitClick = () => {
    const { callGetState } = props;
    callGetState(async (state) => {
      const { rentRecordList } = state;
      if (rentRecordList && rentRecordList.length) {
        const err = validator(state);
        if (err) {
          message.error(err);
          return;
        }
        // 包含直连预订订单
        if (hasDirectChannel(rentRecordList)) {
          if (checkDirectCancelPermission()) {
            showDirectCancelConfirm(confirmPortal).then((isConfirm) => {
              if (isConfirm) {
                submitCancelData(rentRecordList);
              }
            });
          } else {
            showNoPermissionModal(confirmPortal, rentRecordList);
          }
        } else {
          submitCancelData(rentRecordList);
        }
      }
    });
  };

  if (!isInit) return <PortalLoading />;

  return (
    <Spin spinning={isLoading}>
      <DrawerHeader title={intl.get('drawer_reserve_cancel_title')} onClose={handleEditCancel} />
      <div className={styles.pageContainer}>
        <RentRoomList />
      </div>
      <DrawerFooter>
        <ThrottleButton type="primary" className={styles.submitBtn} onClick={handleSubmitClick}>
          {intl.get('drawer_reserve_cancel_btn_cancel')}
        </ThrottleButton>
        <Button onClick={handleEditCancel}>{intl.get('app_confirm_cancel_btn')}</Button>
      </DrawerFooter>
      <SettleDrawer ref={settleDrawer} />
    </Spin>
  );
};

export default () =>
  usePortalDrawer<IConfig>(connect(mapStateToProps, mapDispatchToProps)(memo(Index)));
